import React, { useState, useEffect } from 'react';
import {useMediaQuery, createTheme, TextField, Button, Checkbox, Container, FormControlLabel, InputLabel, Select, MenuItem, Typography, Grid, Box, RadioGroup, Radio } from '@mui/material';
import Error from '../Error/Error';
import Success from '../Success/Success';
import { useTranslation } from 'react-i18next';
import { getOrgranizationalUnits, getEmployees, editEmployee } from '../../api/axios';
import { useParams, useNavigate } from 'react-router-dom';

const EditEmployee = () => {
    const { t } = useTranslation('global');
    const { id } = useParams();

    const navigate = useNavigate();
    const theme = createTheme({
            breakpoints: {
                values: {
                xs: 0,
                sm: 600,
                md: 900,
                lg: 1200,
                xl: 1536,
                xxl: 1650
                },
            },
        })
    const isSmall = useMediaQuery(theme.breakpoints.down("md"));

    const [employeeId, setEmployeeId] = useState('');
    const [cardNo, setCardNo] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [JMBG, setJMBG] = useState('');
    const [active, setActive] = useState(false);
    const [sector, setSector] = useState('');
    const [organizationalUnit, setOrganizationalUnit] = useState('');
    const [workplace, setWorkplace] = useState('');
    const [section, setSection] = useState('');
    const [cardLimitType, setCardLimitType] = useState('1'); // Default to basic card
    const [limit, setLimit] = useState();
    const [dailyLimit, setDailyLimit] = useState();
    const [remainingCredit, setRemainingCredit] = useState();
    const [isHoliday, setIsHoliday] = useState(false);
    const [holiday, setHoliday] = useState('');
    const [holidayDate, setHolidayDate] = useState('');
    const [note, setNote] = useState('');
    const [imageBase64, setImageBase64] = useState("");

    const [orgUnits, setOrgUnits] = useState([]);
    const [errMsg, setErrMsg] = useState('');
    const [succ, setSucc] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch organizational units
                const units = await getOrgranizationalUnits();
                setOrgUnits(units);

                // Fetch employee data
                const employees = await getEmployees();
                const employee = employees.find(e => e.id.toString() === id);
                if (employee) {
                    setImageBase64(employee.photo);
                    setEmployeeId(employee.id);
                    setCardNo(employee.serial_num);
                    setFirstName(employee.name);
                    setLastName(employee.last_name);
                    setJMBG(employee.jmbg);
                    const isActive = Boolean(employee.active);
                    setActive(isActive);
                    setSector(employee.sector);
                    setOrganizationalUnit(employee.organization_unit_id);
                    setWorkplace(employee.workplace);
                    setSection(employee.section);
                    console.log('Employee limit_type:', employee.limit_type);
                    const limitType = employee.limit_type !== undefined ? employee.limit_type.toString() : '1';
                    console.log('Converted limit_type:', limitType);
                    if (['1', '2', '3'].includes(limitType)) {
                        setCardLimitType(limitType);
                    } else {
                        console.warn('Invalid limit_type:', limitType);
                        setCardLimitType('1');
                    }
                    setLimit(employee.monthly_limit_id);
                    setDailyLimit(employee.daily_limit);
                    setRemainingCredit(employee.credit);
                    setIsHoliday(!!employee.holiday_name);
                    setHoliday(employee.holiday_name);
                    setHolidayDate(employee.holiday_date);
                    setNote(employee.note);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [id]);

    const handleActiveChange = (e) => {
        setActive(!active);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!employeeId || !firstName || !lastName || !JMBG) {
            setErrMsg(t("newEmployee.requiredFields"));
            return;
        }

        const updatedEmployee = {
            id: parseInt(employeeId),
            serial_num: cardNo,
            name: firstName,
            last_name: lastName,
            jmbg: JMBG,
            active: active ? 1 : 0,
            sector,
            organization_unit_id: parseInt(organizationalUnit),
            workplace,
            section,
            limit_type: parseInt(cardLimitType),
            daily_limit: parseFloat(dailyLimit) || 0,
            monthly_limit_id: parseInt(limit) || 0,
            credit: parseFloat(remainingCredit) || 0,
            holiday_name: holiday,
            holiday_date: holidayDate,
            note,
            photo: imageBase64
        };

        try {
            const response = await editEmployee(updatedEmployee);
            if (response.success) {
                setSucc(t("editEmployee.successfullyUpdated"));
                setTimeout(() => setSucc(''), 3000);
            } else {
                setErrMsg(t("editEmployee.updateFailed"));
                setTimeout(() => setErrMsg(''), 3000);
            }
        } catch (error) {
            setErrMsg(t("editEmployee.updateFailed"));
            setTimeout(() => setErrMsg(''), 3000);
        }
    };

    return (
        <Container maxWidth="lg" className="container-employee" sx={{ p: 3, flexShrink: 0 , mt: 4, zoom: {md:0.8, lg: 0.9, xl: 0.9}, '@media (min-width:1650px)': {zoom: 1.2,}}}>
            <Typography variant="h4" sx={{ mb:4 }}>{t("editEmployee.editEmployee")}</Typography>
            <form onSubmit={handleSubmit} style={{ width:"90%", display:"flex", justifyContent:"center", alignItems:"center"}}>
            {isSmall ? (
                        <Grid container direction="column" spacing={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="employee-image-upload-mobile"
                                        type="file"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onload = (event) => {
                                                    setImageBase64(event.target.result);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                    />
                                    <label htmlFor="employee-image-upload-mobile">
                                        {imageBase64 ? (
                                            <Box
                                                component="img"
                                                src={imageBase64}
                                                sx={{
                                                    width: "100%",
                                                    maxHeight: "200px",
                                                    objectFit: "contain",
                                                    boxShadow: 1,
                                                    cursor: 'pointer'
                                                }}
                                                alt="Uploaded image"
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: "140px",
                                                    height: "200px",
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    border: '1px solid gray',
                                                    boxShadow: 1,
                                                    cursor: 'pointer',
                                                    backgroundColor: '#f5f5f5'
                                                }}
                                            >
                                                <Typography fontSize="small">Upload</Typography>
                                            </Box>
                                        )}
                                    </label>
                                </Grid>
            
              <Grid item xs={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label={t("newEmployee.firstName")}
                      fullWidth
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("newEmployee.lastName")}
                      fullWidth
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("newEmployee.JMBG")}
                      fullWidth
                      value={JMBG}
                      onChange={(e) => setJMBG(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            
                            <Grid item>
                                <FormControlLabel
                                label={t("newEmployee.active")}
                                control={
                                    <Checkbox
                                    checked={active}
                                    onChange={handleActiveChange}
                                    value={active}
                                    />
                                }
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                label={t("newEmployee.sector")}
                                fullWidth
                                value={sector}
                                onChange={(e) => setSector(e.target.value)}
                                />
                            </Grid>
                            <Grid item>
                                <Select
                                fullWidth
                                value={organizationalUnit}
                                onChange={(e) => setOrganizationalUnit(e.target.value)}
                                displayEmpty
                                >
                                <MenuItem value="" disabled>
                                    {t("newEmployee.selectOrganizationalUnit")}
                                </MenuItem>
                                {orgUnits.map((orgUnit) => (
                                    <MenuItem key={orgUnit.id} value={orgUnit.id}>
                                    {orgUnit.name}
                                    </MenuItem>
                                ))}
                                </Select>
                            </Grid>
                            <Grid item>
                                <TextField
                                label={t("newEmployee.workplace")}
                                fullWidth
                                value={workplace}
                                onChange={(e) => setWorkplace(e.target.value)}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                label={t("newEmployee.section")}
                                fullWidth
                                value={section}
                                onChange={(e) => setSection(e.target.value)}
                                />
                            </Grid>
                            <Grid item>
                                <RadioGroup
                                value={cardLimitType}
                                onChange={(e) => setCardLimitType(e.target.value)}
                                >
                                <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("newEmployee.basicCard")}
                                />
                                <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("newEmployee.monthLimitedCard")}
                                />
                                <FormControlLabel
                                    value="3"
                                    control={<Radio />}
                                    label={t("newEmployee.unlimitedCard")}
                                />
                                </RadioGroup>
                            </Grid>
                            <Grid item>
                                <TextField
                                label={t("newEmployee.limit")}
                                fullWidth
                                value={limit}
                                onChange={(e) => setLimit(e.target.value)}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                label={t("newEmployee.dailyLimit")}
                                fullWidth
                                value={dailyLimit}
                                onChange={(e) => setDailyLimit(e.target.value)}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                label={t("newEmployee.remainingCredit")}
                                fullWidth
                                value={remainingCredit}
                                onChange={(e) => setRemainingCredit(e.target.value)}
                                disabled
                                />
                            </Grid>
                            <Grid item>
                                <FormControlLabel
                                control={
                                    <Checkbox
                                    checked={isHoliday}
                                    onChange={(e) => setIsHoliday(e.target.checked)}
                                    value={t("newEmployee.isHoliday")}
                                    />
                                }
                                label={t("newEmployee.isHoliday")}
                                />
                            </Grid>
                            {isHoliday && (
                                <>
                                <Grid item>
                                    <TextField
                                    label={t("newEmployee.holiday")}
                                    fullWidth
                                    value={holiday}
                                    onChange={(e) => setHoliday(e.target.value)}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                    type="date"
                                    fullWidth
                                    label={t("newEmployee.holidayDate")}
                                    value={holidayDate}
                                    onChange={(e) => setHolidayDate(e.target.value)}
                                    />
                                </Grid>
                                </>
                            )}
                            <Grid item>
                                <TextField
                                label={t("newEmployee.note")}
                                fullWidth
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                label={t("newEmployee.cardNo")}
                                fullWidth
                                value={cardNo}
                                onChange={(e) => setCardNo(e.target.value)}
                                required
                                title={t("newEmployee.cardNo")}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Button fullWidth variant="contained" type="submit">
                                {t("edit.save")}
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                                                    <Button
                                                                    fullWidth
                                                                        type="button"
                                                                        variant="contained"
                                                                        color="error"
                                                                        onClick={() => { navigate('/new-employee') }}
                                                                    >
                                                                        {t("editReader.cancel")}
                                                                    </Button>
                                                                    </Grid>
                            </Grid>
            
                    ) : (
                        <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Grid container direction="row" alignItems="center" spacing={2}>
                                                <Grid item xs={8}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label={t("newEmployee.firstName")}
                                                                fullWidth
                                                                value={firstName}
                                                                onChange={(e) => setFirstName(e.target.value)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label={t("newEmployee.lastName")}
                                                                fullWidth
                                                                value={lastName}
                                                                onChange={(e) => setLastName(e.target.value)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label={t("newEmployee.JMBG")}
                                                                fullWidth
                                                                value={JMBG}
                                                                onChange={(e) => setJMBG(e.target.value)}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={4} sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                                                    <input
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        id="employee-image-upload"
                                                        type="file"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                const reader = new FileReader();
                                                                reader.onload = (event) => {
                                                                    setImageBase64(event.target.result);
                                                                };
                                                                reader.readAsDataURL(file);
                                                            }
                                                        }}
                                                    />
                                                    <label htmlFor="employee-image-upload">
                                                        {imageBase64 ? (
                                                            <Box
                                                                component="img"
                                                                src={imageBase64}
                                                                sx={{
                                                                    maxWidth: "100%",
                                                                    maxHeight: "300px",
                                                                    objectFit: "contain",
                                                                    boxShadow: 1,
                                                                    mb: 2,
                                                                    cursor: 'pointer'
                                                                }}
                                                                alt="Uploaded image"
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: "130px",
                                                                    height: "200px",
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center',
                                                                    border: '1px solid gray',
                                                                    boxShadow: 1,
                                                                    mb: 2,
                                                                    cursor: 'pointer',
                                                                    backgroundColor: '#f5f5f5'
                                                                }}
                                                            >
                                                                <Typography>Upload image</Typography>
                                                            </Box>
                                                        )}
                                                    </label>
                                                    <FormControlLabel
                                                        label={t("newEmployee.active")}
                                                        control={
                                                            <Checkbox
                                                                checked={active}
                                                                onChange={handleActiveChange}
                                                                value={active}
                                                            />
                                                        }
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {/*Sektori*/}
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                label={t("newEmployee.sector")}
                                                fullWidth
                                                value={sector}
                                                onChange={(e) => setSector(e.target.value)}
                                            />
                                        </Grid>
                                        {/*Organizacione jedinice*/}
                                        <Grid item xs={12} sm={12}>
                                            <Select
                                                fullWidth
                                                value={organizationalUnit} // Koristi sektor kao vrednost
                                                onChange={(e) => setOrganizationalUnit(e.target.value)} // Postavi samo ID selektovanog sketora
                                                displayEmpty
                                            >
                                                <MenuItem value="" disabled>
                                                {t("newEmployee.selectOrganizationalUnit")}
                                                </MenuItem>
                                                {orgUnits.map((orgUnit) => (
                                                <MenuItem key={orgUnit.id} value={orgUnit.id}>
                                                    {orgUnit.name}
                                                </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        {/*Radno mesto*/}
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                label={t("newEmployee.workplace")}
                                                fullWidth
                                                value={workplace}
                                                onChange={(e) => setWorkplace(e.target.value)}
                                            />
                                        </Grid>
                                        {/*Pogon*/}
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                label={t("newEmployee.section")}
                                                fullWidth
                                                value={section}
                                                onChange={(e) => setSection(e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} sx={{padding: "1"}}>
                                                        <RadioGroup value={cardLimitType} onChange={(e) => setCardLimitType(e.target.value)}>
                                                            <FormControlLabel value="1" control={<Radio />} label={t("newEmployee.basicCard")} />
                                                            <FormControlLabel value="2" control={<Radio />} label={t("newEmployee.monthLimitedCard")} />
                                                            <FormControlLabel value="3" control={<Radio />} label={t("newEmployee.unlimitedCard")} />
                                                        </RadioGroup>
                                                    </Grid>
                                        <Grid item xs={6}>
                                                <Grid container spacing={1}>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label={t("newEmployee.limit")}
                                                                fullWidth
                                                                value={limit}
                                                                onChange={(e) => setLimit(e.target.value)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label={t("newEmployee.dailyLimit")}
                                                                fullWidth
                                                                value={dailyLimit}
                                                                onChange={(e) => setDailyLimit(e.target.value)}
                                                             />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label={t("newEmployee.remainingCredit")}
                                                                fullWidth
                                                                value={remainingCredit}
                                                                onChange={(e) => setRemainingCredit(e.target.value)}
                                                                disabled
                                                             />
                                                        </Grid>
                                                </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                    control={
                                                    <Checkbox
                                                        checked={isHoliday}
                                                        onChange={(e) => setIsHoliday(e.target.checked)}
                                                        value={t("newEmployee.isHoliday")}
                                                    />
                                                }
                                                label={t("newEmployee.isHoliday")}
                                            />
                                        </Grid>
                                        {isHoliday &&
                                        <><Grid item xs={6}>
                                                <TextField
                                                    label={t("newEmployee.holiday")}
                                                    fullWidth
                                                    value={holiday}
                                                    onChange={(e) => setHoliday(e.target.value)}
                                                />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                type="date"
                                                fullWidth
                                                label={t("newEmployee.holidayDate")}
                                                value={holidayDate}
                                                onChange={(e) => setHolidayDate(e.target.value)}
                                            />
                                        </Grid></>}
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                label={t("newEmployee.note")}
                                                fullWidth
                                                value={note}
                                                onChange={(e) => setNote(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                label={t("newEmployee.cardNo")}
                                                fullWidth
                                                value={cardNo}
                                                onChange={(e) => setCardNo(e.target.value)}
                                                required
                                                title={t("newEmployee.cardNo")}
                                            />
                                        </Grid>
                                    
                                        <Grid item xs={12} sm={6}>
                                            <Button fullWidth variant="contained" type="submit">{t("edit.save")}</Button>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                                                                      <Button
                                                                                        fullWidth
                                                                                        type="button"
                                                                                        variant="contained"
                                                                                        color="error"
                                                                                        onClick={() => { navigate('/new-employee') }}
                                                                                      >
                                                                                        {t("editReader.cancel")}
                                                                                      </Button>
                                                                                    </Grid>
                                    </Grid>
                                </>
                    )}
                
            </form>

            {errMsg && <Error message={errMsg} onClose={() => setErrMsg('')} />}
            {succ && <Success message={succ} onClose={() => setSucc('')} />}
        </Container>
    );
};

export default EditEmployee;
